import { useTheme } from "@emotion/react";
import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Radio,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Grid,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { tokens } from "../../theme";
import Headeer from "../../components/Headeer";
import FormService from "../../services/doctorServices/FormService";
import SnackbarComponent from "../../components/SnackbarComponent";
import TableChartIcon from "@mui/icons-material/TableChart";
import ImageIcon from "@mui/icons-material/Image";
import CartilageMap from "../../components/CartilageMap";
import ImageDrawingAnswer from "../../components/ImageDrawingAnswer";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import RatingScaleSlider from "../../components/RatingScaleSlider";

export default function FormFillOut() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id, formId } = useParams();
  const location = useLocation();
  const selectedPatients = location.state?.selectedPatients || [];
  const formService = new FormService();
  const [formInfo, setFormInfo] = useState([]);
  console.log("Selected Patient To Fill Ou Form", selectedPatients);
  const [answers, setAnswers] = useState({});
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const questionMeta = {
    text: {
      title: "Free Text Question",
      subtitle: "Provide a detailed written answer",
      gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[400]} 100%)`,
      icon: <TextFieldsOutlinedIcon />,
      chipLabel: "Text",
    },
    multiple_choice: {
      title: "Multiple Choice Question",
      subtitle: "Select the option that best matches the patient",
      gradient: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <FormatListBulletedOutlinedIcon />,
      chipLabel: "Multiple Choice",
    },
    table: {
      title: "Table Question",
      subtitle: "Score each row by selecting one option",
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <TableChartIcon />,
      chipLabel: "Table",
    },
    image: {
      title: "Image Question (ICRS)",
      subtitle: "Mark anatomical regions directly on the image",
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <ImageIcon />,
      chipLabel: "Image",
    },
    rating_scale: {
      title: "Rating scale (VAS 0–10)",
      subtitle: "Slide to indicate the level",
      gradient: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <LinearScaleIcon />,
      chipLabel: "VAS",
    },
    default: {
      title: "Question",
      subtitle: "",
      gradient: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
      icon: <DescriptionOutlinedIcon />,
      chipLabel: "Question",
    },
  };

  useEffect(() => {
    const fetchFormInfo = async () => {
      try {
        const response = await formService.getFormInfo(formId);
        setFormInfo(response);
        console.log(response);
      } catch (error) {
        console.log("Form bilgileri getirilirken bir sorun oluştu: ", error);
      }
    };
    fetchFormInfo();
  }, []);

  useEffect(() => {
    if (!formInfo?.questions?.length) return;
    setAnswers((prev) => {
      const next = { ...prev };
      let changed = false;
      formInfo.questions.forEach((q, i) => {
        if (q.type === "rating_scale" && next[i] === undefined) {
          next[i] = 5;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [formInfo?.questions]);

  const handleAnswerChange = (index, value) => {
    setAnswers((prev) => ({
      ...prev,
      [index]: value,
    }));
    console.log(answers);
  };

  // --- Tablo soruları için özel handler (her satırda en fazla 1 seçenek) ---
  const handleTableAnswerChange = (questionIndex, rowIndex, headerIdx, header) => {
    const cellKey = `${rowIndex}_${headerIdx}`;
    setAnswers((prev) => {
      const currentQuestionAnswers = prev[questionIndex] || {};
      const isCurrentlySelected = currentQuestionAnswers[cellKey] === true;
      
      // Yeni state objesi oluştur
      const newQuestionAnswers = { ...currentQuestionAnswers };
      
      if (isCurrentlySelected) {
        // Seçiliyse kaldır
        delete newQuestionAnswers[cellKey];
      } else {
        // Seçili değilse, önce aynı satırdaki diğer seçimleri kaldır
        Object.keys(newQuestionAnswers).forEach(key => {
          if (key.startsWith(`${rowIndex}_`) && key !== cellKey) {
            delete newQuestionAnswers[key];
          }
        });
        // Sonra yeni seçimi ekle
        newQuestionAnswers[cellKey] = true;
      }
      
      return {
        ...prev,
        [questionIndex]: newQuestionAnswers,
      };
    });
  };

  // Form validasyonu
  const validateForm = () => {
    if (!formInfo?.questions || formInfo.questions.length === 0) {
      return { isValid: true, message: "" };
    }

    for (let i = 0; i < formInfo.questions.length; i++) {
      const question = formInfo.questions[i];
      const answer = answers[i];

      // Text soruları için kontrol
      if (question.type === "text") {
        if (!answer || answer.trim() === "") {
          return {
            isValid: false,
            message: `Question ${i + 1} is required. Please provide an answer.`
          };
        }
      }

      // Multiple choice soruları için kontrol
      if (question.type === "multiple_choice") {
        if (!answer || answer === "") {
          return {
            isValid: false,
            message: `Question ${i + 1} is required. Please select an option.`
          };
        }
      }

      // Table soruları için kontrol
      if (question.type === "table") {
        if (!answer || typeof answer !== "object") {
          return {
            isValid: false,
            message: `Question ${i + 1} (Table) is required. Please select an option for each row.`
          };
        }

        // Her satırda en az bir seçim olmalı
        const selectedCells = Object.keys(answer).filter(key => answer[key] === true);
        const selectedRows = new Set(selectedCells.map(cellKey => cellKey.split('_')[0]));
        const totalRows = question.tableRows;

        if (selectedRows.size < totalRows) {
          return {
            isValid: false,
            message: `Question ${i + 1} (Table) is incomplete. Please select an option for all ${totalRows} rows. (${selectedRows.size}/${totalRows} completed)`
          };
        }
      }

      // Image soruları için kontrol
      if (question.type === "image") {
        const mode = question.imageAnswerMode || "select";
        if (mode === "draw") {
          if (!answer || typeof answer !== "string" || answer.trim() === "") {
            return {
              isValid: false,
              message: `Question ${i + 1} (Image) is required. Please draw on the image.`
            };
          }
        } else if (!answer || !Array.isArray(answer) || answer.length === 0) {
          return {
            isValid: false,
            message: `Question ${i + 1} (Image) is required. Please select at least one area.`
          };
        }
      }

      if (question.type === "rating_scale") {
        const v = answer;
        if (v === undefined || v === null || v === "") {
          return {
            isValid: false,
            message: `Question ${i + 1} (Rating scale) is required. Please set the slider.`
          };
        }
        const n = Number(v);
        if (Number.isNaN(n) || n < 0 || n > 10) {
          return {
            isValid: false,
            message: `Question ${i + 1} (Rating scale) must be between 0 and 10.`
          };
        }
      }
    }

    return { isValid: true, message: "" };
  };

  const handleSave = async ()=> {
    // Form validasyonu
    const validation = validateForm();
    if (!validation.isValid) {
      setAlertMessage(validation.message);
      setAlertSeverity("error");
      setShowAlert(true);
      return;
    }

    try {
      const formattedQuestions = formInfo?.questions?.map((q, index) => {
        let selectedAnswer = answers[index] || "";

        // multiple_choice sorularında seçilen option'un level'ini bul
        let selectedLevel = null;
        if (q.type === "multiple_choice") {
          const selectedOption = q.options.find(
            (opt) => opt.option === selectedAnswer
          );
          selectedLevel = selectedOption ? selectedOption.option_level : 0;
        }

        if (q.type === "rating_scale") {
          const num = Number(answers[index]);
          const v = Number.isNaN(num)
            ? 0
            : Math.max(0, Math.min(10, Math.round(num)));
          selectedAnswer = v;
          const imp = Array.isArray(q.scaleImportances) ? q.scaleImportances : [];
          const raw = Number(imp[v]);
          selectedLevel = Number.isNaN(raw) ? 0 : raw;
        }

        // table sorularında her satır için cevapları formatla
        if (q.type === "table") {
          const tableAnswers = answers[index] || {};
          // Her hücre için "rowIndex_colIndex": "headerValue" formatında gönder
          const formattedTableAnswers = {};
          
          Object.keys(tableAnswers).forEach(cellKey => {
            if (tableAnswers[cellKey] === true) {
              const [rowIdx, headerIdx] = cellKey.split('_');
              const rowIndex = parseInt(rowIdx);
              const headerIndex = parseInt(headerIdx);
              
              // Her hücre için benzersiz key: "rowIndex_colIndex"
              formattedTableAnswers[cellKey] = q.tableHeaders[headerIndex];
            }
          });
          
          selectedAnswer = JSON.stringify(formattedTableAnswers);
        }

        // image sorularında seçilen alanları formatla
        if (q.type === "image") {
          const mode = q.imageAnswerMode || "select";
          if (mode === "draw") {
            selectedAnswer = answers[index] || "";
            selectedLevel = null;
          } else {
            const selectedAreas = answers[index] || [];
            selectedAnswer = JSON.stringify(selectedAreas); // Seçilen alanları JSON string olarak kaydet
            selectedLevel = selectedAreas.reduce((total, area) => {
              const areaValue = parseFloat(area.value) || 0;
              return total + areaValue;
            }, 0);
          }
        }

        return {
          question: q.question,
          answer: selectedAnswer,
          type: q.type,
          option_level: selectedLevel, // ✅ seçilen option/area'ların level'i
          ...(q.type === "table" && {
            tableHeaders: q.tableHeaders,
            tableQuestions: q.tableQuestions,
            tableRows: q.tableRows,
            tableCols: q.tableCols,
            tableHeaderImportance: q.tableHeaderImportance, // ✅ tablo importance değerlerini ekle
          }),
          ...(q.type === "image" && {
            imageUrl: q.imageUrl,
            imageAnswerMode: q.imageAnswerMode || "select",
            areas: q.areas,
          }),
          ...(q.type === "rating_scale" && {
            value: typeof selectedAnswer === "number" ? selectedAnswer : Number(selectedAnswer),
            scaleImportances: q.scaleImportances,
            minLabel: q.minLabel,
            maxLabel: q.maxLabel,
            positiveEnd: q.positiveEnd || "min",
          }),
        };
      });

      const data = {
        name: formInfo?.name,
        description: formInfo?.description,
        questions: formattedQuestions,
        type: formInfo?.type,
        form_id: formInfo?.id || formId,
        patient_id: selectedPatients[0]?.id,
        // ❌ total_form_score kaldırıldı - Backend hesaplayacak
      };

      const response = await formService.formAnswer(data);
      console.log("Form başarıyla gönderildi:", response);
      setAlertMessage("Form Saved Successfully!");
      setAlertSeverity("success");
      setShowAlert(true);
      setTimeout(() => {
        navigate(-1);
      }, 3000);
    } catch (error) {
      console.error("Form gönderilemedi:", error);
      setAlertMessage("Error saving form. Please try again.");
      setAlertSeverity("error");
      setShowAlert(true);
    }
  };

  const handleCloseSnackbar = () => {
    setShowAlert(false);
  };

  const patient = selectedPatients[0] || {};
  const patientFullName = patient
    ? `${patient.firstName || "—"} ${patient.lastName || ""}`.trim() || "—"
    : "—";
  const patientEmail = patient?.email || "—";
  const fileName = formInfo?.File?.name || "—";
  const formTypeLabel = formInfo?.type === "for me" ? "For Doctor" : "For Patient";

  return (
    <Box sx={{ marginLeft: 2, marginRight: 2 }}>
      <Headeer
        title={"Fill Out Form"}
        subtitle={"Fill Out the Patient-Specific Form"}
      />
      <Box sx={{ width: "90%", mx: "auto", mt: 3 }}>
        <Box
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: `0 16px 36px ${colors.primary[900]}50`,
            border: `1px solid ${colors.primary[200]}30`,
            backgroundColor: colors.primary[500],
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              px: { xs: 2.5, md: 3.5 },
              py: 2.5,
              background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[500]} 100%)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: 3,
                  backgroundColor: colors.primary[500],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.grey[100],
                  boxShadow: `0 10px 26px ${colors.primary[900]}55`,
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    fontSize: "1.1rem",
                  }}
                >
                  Patient Overview
                </Typography>
                <Typography sx={{ color: colors.grey[200], opacity: 0.85 }}>
                  Confirm patient and file information before completing the form.
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <Chip
                icon={<PersonOutlineIcon sx={{ fontSize: 18 }} />}
                label={patientFullName}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.2,
                }}
              />
              <Chip
                icon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />}
                label={patientEmail}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.2,
                }}
              />
              <Chip
                icon={<FolderOpenOutlinedIcon sx={{ fontSize: 18 }} />}
                label={fileName}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.2,
                }}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              py: 3,
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: colors.grey[400],
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Patient Name
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={patientFullName}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        borderRadius: 3,
                        backgroundColor: colors.primary[500],
                        color: colors.grey[100],
                        "& fieldset": {
                          borderColor: `${colors.primary[200]}33`,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: colors.grey[400],
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Patient Email
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={patientEmail}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        borderRadius: 3,
                        backgroundColor: colors.primary[500],
                        color: colors.grey[100],
                        "& fieldset": {
                          borderColor: `${colors.primary[200]}33`,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: colors.grey[400],
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    File Name
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={fileName}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        borderRadius: 3,
                        backgroundColor: colors.primary[500],
                        color: colors.grey[100],
                        "& fieldset": {
                          borderColor: `${colors.primary[200]}33`,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: colors.grey[400],
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Form Name
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formInfo?.name || "—"}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        borderRadius: 3,
                        backgroundColor: colors.primary[500],
                        color: colors.grey[100],
                        "& fieldset": {
                          borderColor: `${colors.primary[200]}33`,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
              {patient?.dateOfBirth && (
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        color: colors.grey[400],
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Date of Birth
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={patient.dateOfBirth}
                      InputProps={{
                        readOnly: true,
                        sx: {
                          borderRadius: 3,
                          backgroundColor: colors.primary[500],
                          color: colors.grey[100],
                          "& fieldset": {
                            borderColor: `${colors.primary[200]}33`,
                          },
                        },
                      }}
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: colors.grey[400],
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Form Type
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={formTypeLabel}
                    InputProps={{
                      readOnly: true,
                      sx: {
                        borderRadius: 3,
                        backgroundColor: colors.primary[500],
                        color: colors.grey[100],
                        "& fieldset": {
                          borderColor: `${colors.primary[200]}33`,
                        },
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>

      {/*Form Kısmı Doldur*/}

      <Box sx={{ width: "90%", ml: 7, mt: 5 }}>
        <Box
          sx={{
            mb: 4,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: `0 14px 32px ${colors.primary[900]}40`,
            border: `1px solid ${colors.primary[200]}30`,
            backgroundColor: colors.primary[500],
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: { xs: 2.5, md: 3.5 },
              py: 2.5,
              background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: 3,
                  backgroundColor: colors.primary[500],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.grey[100],
                  boxShadow: `0 10px 26px ${colors.primary[900]}55`,
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    fontSize: "1.1rem",
                  }}
                >
                  Form Details
                </Typography>
                <Typography sx={{ color: colors.grey[200], opacity: 0.85 }}>
                  Review the form information before submitting responses.
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Chip
                label={`File: ${fileName}`}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 1.2,
                }}
              />
              <Chip
                label={`Patient: ${patientFullName}`}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 1.2,
                }}
              />
              <Chip
                label={`Type: ${formTypeLabel}`}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 1.2,
                }}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              py: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: colors.grey[300],
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Form Title
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={formInfo?.name || "—"}
                InputProps={{
                  readOnly: true,
                  sx: {
                    borderRadius: 3,
                    backgroundColor: colors.primary[500],
                    fontSize: "1.6rem",
                    fontWeight: 700,
                    color: colors.grey[100],
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}44`,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.greenAccent[400],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.greenAccent[500],
                      boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "right", mt: 0.5, color: colors.grey[400] }}
              >
                {(formInfo?.name?.length ?? 0)} characters
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: colors.grey[300],
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Form Description
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                multiline
                minRows={3}
                value={formInfo?.description || "—"}
                InputProps={{
                  readOnly: true,
                  sx: {
                    borderRadius: 3,
                    backgroundColor: colors.primary[500],
                    color: colors.grey[200],
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}33`,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.blueAccent[300],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.blueAccent[200],
                      boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "right", mt: 0.5, color: colors.grey[400] }}
              >
                {(formInfo?.description?.length ?? 0)} characters
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                backgroundColor: `${colors.primary[600]}66`,
                borderRadius: 3,
                border: `1px solid ${colors.primary[200]}22`,
                p: 2.5,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[400], textTransform: "uppercase" }}>
                  Patient Email
                </Typography>
                <Typography sx={{ color: colors.grey[100], fontWeight: 600 }}>
                  {patientEmail}
                </Typography>
              </Box>
              {patient?.dateOfBirth && (
                <Box>
                  <Typography variant="caption" sx={{ color: colors.grey[400], textTransform: "uppercase" }}>
                    Date of Birth
                  </Typography>
                  <Typography sx={{ color: colors.grey[100], fontWeight: 600 }}>
                    {patient.dateOfBirth}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[400], textTransform: "uppercase" }}>
                  Form Type
                </Typography>
                <Typography sx={{ color: colors.grey[100], fontWeight: 600 }}>
                  {formTypeLabel}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box>
          {formInfo?.questions?.map((field, index) => {
            const answerValue = answers[index];
            const meta = questionMeta[field.type] || questionMeta.default;
            const tableAnswerCount =
              field.type === "table" && answerValue && typeof answerValue === "object"
                ? new Set(Object.keys(answerValue).filter(key => answerValue[key] === true).map(cellKey => cellKey.split('_')[0])).size
                : 0;
            const imageMode = field.imageAnswerMode || "select";
            const imageAnswerCount =
              field.type === "image" && Array.isArray(answerValue) ? answerValue.length : 0;
            const imageHasDrawing =
              field.type === "image" && imageMode === "draw" && typeof answerValue === "string" && answerValue.trim() !== "";
            const isAnswered =
              field.type === "text"
                ? Boolean(
                    answerValue &&
                      typeof answerValue === "string" &&
                      answerValue.trim() !== ""
                  )
                : field.type === "multiple_choice"
                ? Boolean(answerValue && answerValue !== "")
                : field.type === "table"
                ? tableAnswerCount === field.tableRows
                : field.type === "rating_scale"
                ? typeof answerValue === "number" && !Number.isNaN(answerValue)
                : field.type === "image"
                ? (imageMode === "draw" ? imageHasDrawing : imageAnswerCount > 0)
                : Boolean(answerValue);

            return (
              <Box key={index} sx={{ marginBottom: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        background: meta.gradient,
                        color: colors.grey[100],
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 6px 16px ${colors.primary[900]}40`,
                      }}
                    >
                      {meta.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ mb: 0.2, color: colors.grey[100], fontWeight: 700 }}>
                        Question {index + 1}
                      </Typography>
                      {meta.subtitle && (
                        <Typography variant="body2" sx={{ color: colors.grey[300], opacity: 0.85 }}>
                          {meta.title}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
                    <Chip
                      label={meta.chipLabel}
                      sx={{
                        backgroundColor: `${colors.primary[500]}aa`,
                        color: colors.grey[100],
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 1.5,
                      }}
                    />
                    <Chip
                      label={isAnswered ? "Answered" : "Pending"}
                      sx={{
                        backgroundColor: isAnswered
                          ? `${colors.greenAccent[600]}aa`
                          : `${colors.redAccent[600]}aa`,
                        color: colors.grey[100],
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 1.5,
                      }}
                    />
                    {field.type === "table" && (
                      <Chip
                        label={`${tableAnswerCount}/${field.tableRows} rows`}
                        sx={{
                          backgroundColor: `${colors.primary[500]}aa`,
                          color: colors.grey[100],
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 1.5,
                        }}
                      />
                    )}
                    {field.type === "image" && (
                      <Chip
                        label={imageMode === "draw" ? (imageHasDrawing ? "Drawing ready" : "No drawing") : `${imageAnswerCount} areas`}
                        sx={{
                          backgroundColor: `${colors.primary[500]}aa`,
                          color: colors.grey[100],
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 1.5,
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              <Box
                sx={{
                  borderRadius: 5,
                  boxShadow: 10,
                  border: `2px solid ${colors.primary[100]}`,
                  borderTop: `3px solid`,
                  borderLeft: `4px solid ${colors.greenAccent[400]}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    ml: 2,
                    mt: 2,
                    mb: 0.5,
                    color: colors.grey[300],
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  Question Text
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={field.question}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    maxWidth: 760,
                    m: 2,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: colors.primary[500],
                      fontSize: "1.05rem",
                      fontWeight: 600,
                      color: colors.grey[100],
                      "& fieldset": {
                        borderColor: `${colors.primary[200]}44`,
                      },
                    },
                  }}
                />
                {field.type === "text" && (
                  <Box sx={{ maxWidth: 760, m: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        color: colors.grey[300],
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Answer
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Enter your answer"
                      value={answerValue || ""}
                      onChange={(e) => {
                        handleAnswerChange(index, e.target.value);
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                          backgroundColor: colors.primary[500],
                          color: colors.grey[100],
                          "& fieldset": {
                            borderColor: `${colors.primary[200]}33`,
                          },
                          "&:hover fieldset": {
                            borderColor: colors.greenAccent[400],
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: colors.greenAccent[500],
                            boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                          },
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        textAlign: "right",
                        mt: 0.5,
                        color: colors.grey[400],
                      }}
                    >
                      {(answerValue?.length ?? 0)} characters
                    </Typography>
                  </Box>
                )}
                {field.type === "multiple_choice" && (
                  <Box sx={{ m: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        color: colors.grey[300],
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Options
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {field.options.map((option, optionIdx) => {
                        const isSelected = answerValue === option.option;
                        return (
                          <Box
                            key={optionIdx}
                            onClick={() => handleAnswerChange(index, option.option)}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xs: "auto 1fr" },
                              gap: 2,
                              alignItems: "center",
                              borderRadius: 3,
                              border: `1px solid ${colors.primary[200]}33`,
                              boxShadow: `0 8px 18px ${colors.primary[900]}25`,
                              px: { xs: 1.5, md: 2.5 },
                              py: { xs: 1.2, md: 1.5 },
                              backgroundColor: isSelected
                                ? `${colors.greenAccent[600]}33`
                                : `${colors.primary[600]}AA`,
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: `0 10px 24px ${colors.primary[900]}35`,
                              },
                            }}
                          >
                            <Radio
                              checked={isSelected}
                              onChange={() => handleAnswerChange(index, option.option)}
                              color="success"
                            />
                            <Typography
                              sx={{
                                color: colors.grey[100],
                                fontWeight: isSelected ? 700 : 500,
                              }}
                            >
                              {option.option}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {field.type === "rating_scale" && (
                  <Box sx={{ m: 3, maxWidth: 760 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 2,
                        color: colors.grey[300],
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Your answer (0–10)
                    </Typography>
                    <RatingScaleSlider
                      value={
                        typeof answerValue === "number" && !Number.isNaN(answerValue)
                          ? answerValue
                          : 5
                      }
                      onChange={(n) => handleAnswerChange(index, n)}
                      minLabel={field.minLabel || "0"}
                      maxLabel={field.maxLabel || "10"}
                      positiveEnd={field.positiveEnd === "max" ? "max" : "min"}
                      colors={colors}
                    />
                  </Box>
                )}

                {field.type === "image" && (
                  <Box sx={{ margin: 3 }}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2,
                      mb: 3
                    }}>
                      <ImageIcon sx={{ 
                        fontSize: 28,
                        color: colors.greenAccent[500]
                      }} />
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: "bold",
                          background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent"
                        }}
                      >
                        Visual Question (ICRS)
                      </Typography>
                      <Box sx={{ 
                        ml: "auto",
                        display: "flex",
                        gap: 1,
                        alignItems: "center"
                      }}>
                        <Box sx={{ 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 2,
                          backgroundColor: colors.blueAccent[700],
                          border: `1px solid ${colors.blueAccent[500]}`
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                            {imageMode === "draw" ? "✏️ Mode: Draw" : `🎯 Areas: ${field.areas?.length || 0}`}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 2,
                          backgroundColor: colors.greenAccent[700],
                          border: `1px solid ${colors.greenAccent[500]}`
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                            {imageMode === "draw" ? `✓ ${imageHasDrawing ? "Drawing ready" : "No drawing"}` : `✓ Selected: ${answerValue?.length || 0}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {imageMode === "draw" ? (
                      <ImageDrawingAnswer
                        imageUrl={field.imageUrl}
                        value={typeof answerValue === "string" ? answerValue : ""}
                        onChange={(dataUrl) => handleAnswerChange(index, dataUrl)}
                        disabled={false}
                      />
                    ) : (
                      <CartilageMap
                        imageUrl={field.imageUrl}
                        areas={field.areas || []}
                        selectedAreas={answerValue || []}
                        multiSelect={true}
                        onAreaSelect={(area) => {
                          // Alan seçimi/seçim iptali
                          const currentSelected = answerValue || [];
                          const areaIndex = currentSelected.findIndex(a => a.id === area.id);
                          
                          if (areaIndex > -1) {
                            // Alan zaten seçili, kaldır
                            const newSelected = currentSelected.filter(a => a.id !== area.id);
                            handleAnswerChange(index, newSelected);
                          } else {
                            // Alanı ekle
                            handleAnswerChange(index, [...currentSelected, area]);
                          }
                        }}
                        disabled={false}
                        showLabels={true}
                      />
                    )}

                    {/* Seçilen alanlar listesi */}
                    {imageMode !== "draw" && answerValue?.length > 0 && (
                      <Box sx={{ 
                        mt: 3,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: colors.primary[600],
                        border: `2px solid ${colors.greenAccent[700]}`
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: colors.greenAccent[400] }}>
                            ✅ Selected Areas ({answerValue.length})
                          </Typography>
                          
                          {/* Total Score */}
                          <Box sx={{ 
                            px: 2, 
                            py: 1, 
                            borderRadius: 2,
                            backgroundColor: colors.greenAccent[700],
                            border: `2px solid ${colors.greenAccent[500]}`
                          }}>
                            <Typography variant="body1" sx={{ fontWeight: "bold", color: colors.grey[100] }}>
                              💯 Total Score: {answerValue.reduce((total, area) => {
                                const areaValue = parseFloat(area.value) || 0;
                                return total + areaValue;
                              }, 0)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {answerValue.map((area) => (
                            <Chip
                              key={area.id}
                              label={`${area.name} (${area.value || 0})`}
                              onDelete={() => {
                                const newSelected = answerValue.filter(a => a.id !== area.id);
                                handleAnswerChange(index, newSelected);
                              }}
                              sx={{
                                backgroundColor: area.color || colors.greenAccent[700],
                                color: colors.grey[100],
                                fontWeight: 'bold',
                                '& .MuiChip-deleteIcon': {
                                  color: colors.grey[100],
                                  '&:hover': {
                                    color: colors.redAccent[400]
                                  }
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ 
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colors.primary[600],
                      border: `1px solid ${colors.blueAccent[700]}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: colors.blueAccent[400],
                          fontStyle: "italic"
                        }}
                      >
                        {imageMode === "draw"
                          ? "💡 Draw directly on the image. Use Clear if you want to redraw."
                          : "💡 Click on areas to select/deselect. You can select multiple areas."}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {field.type === "table" && (
                  <Box sx={{ margin: 3 }}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2,
                      mb: 3
                    }}>
                      <TableChartIcon sx={{ 
                        fontSize: 28,
                        color: colors.greenAccent[500]
                      }} />
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: "bold",
                          background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent"
                        }}
                      >
                        Table Question
                      </Typography>
                      <Box sx={{ 
                        ml: "auto",
                        display: "flex",
                        gap: 1,
                        alignItems: "center"
                      }}>
                        <Box sx={{ 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 2,
                          backgroundColor: colors.blueAccent[700],
                          border: `1px solid ${colors.blueAccent[500]}`
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                            📝 Rows: {field.tableRows}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 2,
                          backgroundColor: colors.greenAccent[700],
                          border: `1px solid ${colors.greenAccent[500]}`
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                            📊 Columns: {field.tableCols}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      overflowX: "auto",
                      borderRadius: 2,
                      border: `3px solid ${colors.greenAccent[700]}`,
                      boxShadow: `0 4px 16px rgba(0, 0, 0, 0.2)`,
                      background: colors.primary[500]
                    }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ 
                              background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                              fontWeight: "bold",
                              border: `2px solid ${colors.blueAccent[600]}`,
                              color: colors.grey[100],
                              fontSize: "1rem",
                              textAlign: "center",
                              minWidth: 180
                            }}>
                              ❓ Questions
                            </TableCell>
                            {field.tableHeaders.map((header, headerIdx) => (
                              <TableCell 
                                key={headerIdx}
                                sx={{ 
                                  background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                                  fontWeight: "bold",
                                  border: `2px solid ${colors.greenAccent[600]}`,
                                  minWidth: 140,
                                  padding: 2
                                }}
                              >
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
                                  <Typography 
                                    sx={{ 
                                      fontSize: "0.95rem",
                                      fontWeight: "bold",
                                      color: colors.grey[100]
                                    }}
                                  >
                                    {header}
                                  </Typography>
                                  <Box sx={{ 
                                    px: 2, 
                                    py: 0.5, 
                                    borderRadius: 2,
                                    backgroundColor: colors.primary[600],
                                    border: `1px solid ${colors.greenAccent[500]}`
                                  }}>
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        fontWeight: "bold",
                                        color: colors.grey[100]
                                      }}
                                    >
                                      💯 Score: {field.tableHeaderImportance?.[headerIdx] ?? 0}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {field.tableQuestions.map((question, questionIdx) => (
                            <TableRow 
                              key={questionIdx}
                              sx={{
                                "&:hover": {
                                  backgroundColor: colors.primary[600]
                                },
                                transition: "background-color 0.2s"
                              }}
                            >
                              <TableCell sx={{ 
                                backgroundColor: colors.blueAccent[900],
                                border: `1px solid ${colors.blueAccent[700]}`,
                                minWidth: 180,
                                color: colors.grey[100],
                                fontWeight: "500"
                              }}>
                                {question}
                              </TableCell>
                              {field.tableHeaders.map((header, headerIdx) => {
                                const cellKey = `${questionIdx}_${headerIdx}`;
                                const isSelected = answerValue?.[cellKey] === true;
                                
                                // Aynı satırda başka bir hücre seçili mi kontrol et
                                const hasOtherSelectionInRow = Object.keys(answerValue || {})
                                  .some(key => {
                                    if (key === cellKey) return false;
                                    const [rowIdx] = key.split('_');
                                    return rowIdx === String(questionIdx) && answerValue?.[key] === true;
                                  });
                                
                                // Bu hücre disabled olmalı mı? (Aynı satırda başka bir hücre seçiliyse ve bu hücre seçili değilse)
                                const isDisabled = hasOtherSelectionInRow && !isSelected;
                                
                                const handleCellClick = () => {
                                  if (!isDisabled) {
                                    handleTableAnswerChange(
                                      index,
                                      questionIdx,
                                      headerIdx,
                                      header
                                    );
                                  }
                                };
                                
                                return (
                                  <TableCell 
                                    key={headerIdx}
                                    onClick={handleCellClick}
                                    sx={{
                                      border: `2px solid ${isSelected ? colors.greenAccent[500] : colors.greenAccent[700]}40`,
                                      textAlign: "center",
                                      background: isSelected
                                        ? `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`
                                        : isDisabled
                                        ? `linear-gradient(135deg, ${colors.primary[800]} 0%, ${colors.primary[700]} 100%)`
                                        : `linear-gradient(135deg, ${colors.primary[650]} 0%, ${colors.primary[600]} 100%)`,
                                      py: 2.5,
                                      transition: "all 0.2s ease",
                                      cursor: isDisabled ? "not-allowed" : "pointer",
                                      opacity: isDisabled ? 0.5 : 1,
                                      position: "relative",
                                      userSelect: "none",
                                      "&:hover": {
                                        backgroundColor: isDisabled
                                          ? undefined
                                          : isSelected
                                          ? colors.greenAccent[600]
                                          : colors.primary[550],
                                        transform: isDisabled ? undefined : "scale(1.02)",
                                      },
                                    }}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      disabled={isDisabled}
                                      onChange={handleCellClick}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCellClick();
                                      }}
                                      size="large"
                                      sx={{
                                        color: colors.greenAccent[600],
                                        pointerEvents: "none",
                                        "& .MuiSvgIcon-root": {
                                          fontSize: "2rem",
                                        },
                                        "&.Mui-checked": {
                                          color: colors.greenAccent[400],
                                        },
                                        "&.Mui-disabled": {
                                          color: colors.grey[600],
                                        },
                                        "& .MuiTouchRipple-root": {
                                          display: "none",
                                        },
                                      }}
                                    />
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                    <Box sx={{ 
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colors.primary[600],
                      border: `1px solid ${colors.blueAccent[700]}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: colors.blueAccent[400],
                          fontStyle: "italic"
                        }}
                      >
                        💡 Select one option per row
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              </Box>
            );
          })}
        </Box>
        <Box
          onClick={handleSave}
          sx={{ display: "flex", justifyContent: "end" }}
        >
          <Button sx={{ backgroundColor: colors.greenAccent[400] }}>
            Save
          </Button>
        </Box>
        <SnackbarComponent
          severity={alertSeverity}
          message={alertMessage}
          open={showAlert}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
        />
      </Box>
    </Box>
  );
}
